import { BodyHeightUnitChoices } from "@/encounters/components/VitalSigns/zod-schemas/vital-sign";
import { TemperatureUnitChoices } from "@/encounters/constants/vital-sign";
import { WeightUnitChoices } from "@/fhir/weight";
import { convertFeetAndInchesToInches, convertInchesToFeetAndInches } from "@/utils/ucum_units";

/**
 * Unit system chosen by the practitioner for a single vitals form session. It is never persisted:
 * when an existing vitals record is edited the system is inferred back from the saved unit fields.
 */
export type VitalsUnitSystem = "metric" | "imperial";

export const VitalsUnitSystems = {
  Metric: "metric",
  Imperial: "imperial",
} as const satisfies Record<string, VitalsUnitSystem>;

export const DEFAULT_VITALS_UNIT_SYSTEM: VitalsUnitSystem = VitalsUnitSystems.Imperial;

export type WeightUnit = (typeof WeightUnitChoices)[keyof typeof WeightUnitChoices];
export type BodyHeightUnit = (typeof BodyHeightUnitChoices)[keyof typeof BodyHeightUnitChoices];
export type TemperatureUnit = (typeof TemperatureUnitChoices)[keyof typeof TemperatureUnitChoices];

export type VitalsUnitFields = {
  weightUnit: WeightUnit;
  heightUnit: BodyHeightUnit;
  temperatureUnit: TemperatureUnit;
};

const METRIC_UNIT_FIELDS: VitalsUnitFields = {
  weightUnit: WeightUnitChoices.kg,
  heightUnit: BodyHeightUnitChoices.Centimeters,
  temperatureUnit: TemperatureUnitChoices.C,
};

const IMPERIAL_UNIT_FIELDS: VitalsUnitFields = {
  weightUnit: WeightUnitChoices.lbs,
  heightUnit: BodyHeightUnitChoices["Feet and Inches"],
  temperatureUnit: TemperatureUnitChoices.F,
};

const KG_PER_LB = 0.45359237;
const CM_PER_INCH = 2.54;

function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function isPresentNumber(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

export function getMetricUnitFields(): VitalsUnitFields {
  return METRIC_UNIT_FIELDS;
}

export function getImperialUnitFields(): VitalsUnitFields {
  return IMPERIAL_UNIT_FIELDS;
}

export function getVitalsUnitFields(unitSystem: VitalsUnitSystem): VitalsUnitFields {
  return unitSystem === VitalsUnitSystems.Imperial ? IMPERIAL_UNIT_FIELDS : METRIC_UNIT_FIELDS;
}

/** Display suffix for each measurement, e.g. as a `NumberInput` right section. */
export function getVitalsUnitSuffixes(unitSystem: VitalsUnitSystem) {
  return unitSystem === VitalsUnitSystems.Imperial
    ? { weight: "lbs", height: "ft / in", temperature: "°F" }
    : { weight: "kg", height: "cm", temperature: "°C" };
}

export function getTemperatureUnitSuffix(temperatureUnit: TemperatureUnit | null | undefined) {
  return temperatureUnit === TemperatureUnitChoices.C ? "°C" : "°F";
}

type SavedVitalsUnitFields = {
  weightUnit?: string | null;
  heightUnit?: string | null;
  temperatureUnit?: string | null;
};

const IMPERIAL_UNITS: ReadonlySet<string> = new Set<string>([
  WeightUnitChoices.lbs,
  BodyHeightUnitChoices["Feet and Inches"],
  TemperatureUnitChoices.F,
]);

const METRIC_UNITS: ReadonlySet<string> = new Set<string>([
  WeightUnitChoices.kg,
  WeightUnitChoices.g,
  BodyHeightUnitChoices.Centimeters,
  TemperatureUnitChoices.C,
]);

/**
 * Reads the unit system back from saved unit fields so editing an existing record keeps the units it
 * was recorded in. Records mixing both systems, or carrying no units at all, fall back to Imperial.
 */
export function inferVitalsUnitSystem({
  weightUnit,
  heightUnit,
  temperatureUnit,
}: SavedVitalsUnitFields): VitalsUnitSystem {
  const savedUnits = [weightUnit, heightUnit, temperatureUnit].filter(
    (unit): unit is string => !!unit,
  );

  const hasImperial = savedUnits.some((unit) => IMPERIAL_UNITS.has(unit));
  const hasMetric = savedUnits.some((unit) => METRIC_UNITS.has(unit));

  if (hasImperial && !hasMetric) return VitalsUnitSystems.Imperial;
  if (hasMetric && !hasImperial) return VitalsUnitSystems.Metric;

  return DEFAULT_VITALS_UNIT_SYSTEM;
}

/** Converts weight between kg/g (metric) and lbs (imperial). Grams are normalized via kg. */
export function convertWeightValue(
  value: number | null | undefined,
  fromUnit: string | null | undefined,
  toSystem: VitalsUnitSystem,
): number | undefined {
  if (!isPresentNumber(value)) return undefined;

  let kilograms = value;
  if (fromUnit === WeightUnitChoices.lbs) {
    kilograms = value * KG_PER_LB;
  } else if (fromUnit === WeightUnitChoices.g) {
    kilograms = value / 1000;
  } else if (fromUnit !== WeightUnitChoices.kg && fromUnit) {
    // Unrecognized unit: leave the typed number alone.
    return value;
  }

  if (toSystem === VitalsUnitSystems.Imperial) {
    return roundTo(kilograms / KG_PER_LB, 2);
  }

  return roundTo(kilograms, 2);
}

export type HeightMeasurement = {
  heightCm?: number | null;
  heightFeet?: number | null;
  heightInches?: number | null;
};

/** Converts height between cm and feet/inches. */
export function convertHeightMeasurement(
  height: HeightMeasurement,
  fromUnit: string | null | undefined,
  toSystem: VitalsUnitSystem,
): {
  heightCm: number | undefined;
  heightFeet: number | undefined;
  heightInches: number | undefined;
} {
  const toImperial = toSystem === VitalsUnitSystems.Imperial;

  if (toImperial) {
    let totalInches: number | undefined;
    if (
      fromUnit === BodyHeightUnitChoices.Centimeters ||
      (!fromUnit && isPresentNumber(height.heightCm))
    ) {
      if (isPresentNumber(height.heightCm)) {
        totalInches = height.heightCm / CM_PER_INCH;
      }
    } else if (isPresentNumber(height.heightFeet) || isPresentNumber(height.heightInches)) {
      totalInches = convertFeetAndInchesToInches(height.heightFeet ?? 0, height.heightInches ?? 0);
    }

    if (totalInches == null) {
      return { heightCm: undefined, heightFeet: undefined, heightInches: undefined };
    }

    const { feet, inches } = convertInchesToFeetAndInches(roundTo(totalInches, 2));
    return { heightCm: undefined, heightFeet: feet, heightInches: inches };
  }

  let centimeters: number | undefined;
  if (
    fromUnit === BodyHeightUnitChoices["Feet and Inches"] ||
    isPresentNumber(height.heightFeet) ||
    isPresentNumber(height.heightInches)
  ) {
    if (isPresentNumber(height.heightFeet) || isPresentNumber(height.heightInches)) {
      const totalInches = convertFeetAndInchesToInches(
        height.heightFeet ?? 0,
        height.heightInches ?? 0,
      );
      centimeters = totalInches * CM_PER_INCH;
    }
  } else if (isPresentNumber(height.heightCm)) {
    centimeters = height.heightCm;
  }

  return {
    heightCm: centimeters == null ? undefined : roundTo(centimeters, 1),
    heightFeet: undefined,
    heightInches: undefined,
  };
}

/** Converts temperature between °C and °F. */
export function convertTemperatureValue(
  value: number | null | undefined,
  fromUnit: string | null | undefined,
  toSystem: VitalsUnitSystem,
): number | undefined {
  if (!isPresentNumber(value)) return undefined;

  const toImperial = toSystem === VitalsUnitSystems.Imperial;
  const fromFahrenheit = fromUnit === TemperatureUnitChoices.F;

  if (toImperial) {
    if (fromFahrenheit) return roundTo(value, 1);
    return roundTo((value * 9) / 5 + 32, 1);
  }

  if (fromFahrenheit) return roundTo(((value - 32) * 5) / 9, 1);
  return roundTo(value, 1);
}
